import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { programKeys } from './usePrograms';
import type {
  Material,
  Program,
  ProgramLevel,
  LearningFlow,
  StorageProvider,
} from '@lms/shared/types';

export const materialKeys = {
  all: ['materials'] as const,
  lists: () => [...materialKeys.all, 'list'] as const,
  list: (params: Record<string, unknown>) => [...materialKeys.all, 'list', params] as const,
  library: (params: MaterialLibraryFilters) => [...materialKeys.all, 'library', params] as const,
  details: () => [...materialKeys.all, 'detail'] as const,
  detail: (id: string) => [...materialKeys.details(), id] as const,
};

export interface MaterialLibraryFilters {
  page: number;
  limit: number;
  type?: Material['type'];
  search?: string;
}

export function useMaterials(flowId?: string) {
  return useQuery({
    queryKey: materialKeys.list({ flowId }),
    queryFn: () => apiClient.materials.list({ flowId }),
  });
}

export function useMaterialLibrary(filters: MaterialLibraryFilters) {
  return useQuery({
    queryKey: materialKeys.library(filters),
    queryFn: () => apiClient.materials.listPage(filters),
    placeholderData: previousData => previousData,
  });
}

export function useMaterial(id: string) {
  return useQuery({
    queryKey: materialKeys.detail(id),
    queryFn: () => apiClient.materials.get(id),
    enabled: !!id,
  });
}

export function useMaterialDeletionImpact(id: string, enabled = true) {
  return useQuery({
    queryKey: [...materialKeys.detail(id), 'deletion-impact'],
    queryFn: () => apiClient.materials.getDeletionImpact(id),
    enabled: Boolean(id) && enabled,
  });
}

export function useCreateMaterial(programId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Material>) => apiClient.materials.create(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: materialKeys.lists() });
      if (programId) {
        await queryClient.invalidateQueries({ queryKey: programKeys.detail(programId) });
      }
    },
  });
}

export function useUpdateMaterial(programId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Material> }) =>
      apiClient.materials.update(id, data),
    onSuccess: async (_, { id }) => {
      await queryClient.invalidateQueries({ queryKey: materialKeys.lists() });
      await queryClient.invalidateQueries({ queryKey: materialKeys.detail(id) });
      if (programId) {
        await queryClient.invalidateQueries({ queryKey: programKeys.detail(programId) });
      }
    },
  });
}

export function useDeleteMaterial(programId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, confirmation }: { id: string; confirmation?: string }) =>
      apiClient.materials.delete(id, confirmation),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: materialKeys.lists() });
      if (programId) {
        await queryClient.invalidateQueries({ queryKey: programKeys.detail(programId) });
      }
    },
  });
}

export function useUploadMedia() {
  return useMutation({
    mutationFn: ({ file, provider }: { file: File; provider: StorageProvider }) =>
      apiClient.storage.upload(file, provider),
  });
}

export function useStorageProviders() {
  return useQuery({
    queryKey: ['storage-providers'],
    queryFn: () => apiClient.storage.providers(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useStorageMediaLibrary(params: {
  provider: StorageProvider;
  search?: string;
  cursor?: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ['storage-media-library', params.provider, params.search ?? '', params.cursor ?? ''],
    queryFn: () => apiClient.storage.listMedia({ ...params, limit: 30 }),
    enabled: params.enabled ?? true,
    placeholderData: previousData => previousData,
  });
}

export function useMaterialMedia(materialId: string) {
  return useQuery({
    queryKey: ['material-media', materialId],
    queryFn: () => apiClient.materialMedia.list(materialId),
    enabled: !!materialId,
  });
}

export function useAddMaterialMedia(materialId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof apiClient.materialMedia.create>[0]) =>
      apiClient.materialMedia.create(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['material-media', materialId] });
    },
  });
}

export function useDeleteMaterialMedia(materialId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.materialMedia.delete(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['material-media', materialId] });
    },
  });
}

export function useReorderMaterials(programId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ flowId, materialIds }: { flowId: string; materialIds: string[] }) =>
      apiClient.materials.reorder({ flowId, materialIds }),
    onMutate: async ({ flowId, materialIds }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: programKeys.detail(programId!) });

      // Snapshot the previous value
      const previousProgram = queryClient.getQueryData(programKeys.detail(programId!));

      // Optimistically update to the new value
      if (previousProgram) {
        queryClient.setQueryData(programKeys.detail(programId!), (old: Program | undefined) => {
          if (!old) return old;
          return {
            ...old,
            levels: old.levels?.map((level: ProgramLevel) => ({
              ...level,
              flows: level.flows?.map((flow: LearningFlow) => {
                if (flow.id === flowId) {
                  const materialMap = new Map(
                    (flow.materials ?? []).map((m: Material) => [m.id, m])
                  );
                  return {
                    ...flow,
                    materials: materialIds
                      .map((id, index) => {
                        const material = materialMap.get(id);
                        if (material) {
                          return { ...material, order: index + 1 };
                        }
                        return null;
                      })
                      .filter(Boolean),
                  };
                }
                return flow;
              }),
            })),
          };
        });
      }

      return { previousProgram };
    },
    onError: (err, variables, context) => {
      if (context?.previousProgram) {
        queryClient.setQueryData(programKeys.detail(programId!), context.previousProgram);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: materialKeys.lists() });
      if (programId) {
        void queryClient.invalidateQueries({ queryKey: programKeys.detail(programId) });
      }
    },
  });
}

export function useImportMaterial(programId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { sourceMaterialId: string; targetFlowId: string }) =>
      apiClient.materials.importMaterial(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: materialKeys.lists() });
      if (programId) {
        await queryClient.invalidateQueries({ queryKey: programKeys.detail(programId) });
      }
    },
  });
}
